import { useEffect, useRef, useState } from "react";
import { allUsersApi, searchUserApi } from "./Api";
import ProfileModal from "./ProfileModal";

function SearchModal({ onClose }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedTerm, setDebouncedTerm] = useState("");
  const [results, setResults] = useState([]);
  const [suggested, setSuggested] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const modalRef = useRef(null);
  const profileModalRef = useRef(null);
  const actionModalRef = useRef(null);
  const inputRef = useRef(null);

  // Focus input on open
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Close modal on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target) &&
        (!profileModalRef.current || !profileModalRef.current.contains(event.target)) &&
        (!actionModalRef.current || !actionModalRef.current.contains(event.target))
      ) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  // Debounce search term
  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedTerm(searchTerm), 300);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  // Fetch search results & suggested users
  useEffect(() => {
    const fetchUsers = async () => {
      if (!debouncedTerm.trim()) {
        setResults([]);
        setSuggested([]);
        return;
      }

      setLoading(true);
      setError("");

      try {
        const [searchData, allUsers] = await Promise.all([
          searchUserApi(debouncedTerm),
          allUsersApi(),
        ]);

        setResults(searchData);

        // Suggested users: all users not in search results
        const foundIds = new Set(searchData.map((u) => u.id));
        const suggestedUsers = allUsers.filter((u) => !foundIds.has(u.id));
        setSuggested(suggestedUsers);
      } catch (err) {
        setError(err.message || "Something went wrong");
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [debouncedTerm]);

  const handleUserClick = (user) => setSelectedUser(user);
  const closeProfileModal = () => setSelectedUser(null);
  const renderUserItem = (user) => {
    return (
      <li
        key={user.id}
        onClick={() => handleUserClick(user)}
        style={{
          padding: "10px 0",
          borderBottom: "1px solid #ddd",
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          gap: "12px",
        }}
      >
        {user.profile_image ? (
          <img
            src={`${user.profile_image}?t=${Date.now()}`} // cache-busting
            alt={user.full_name || "Avatar"}
            style={{ width: "40px", height: "40px", borderRadius: "50%", objectFit: "cover" }}
          />
        ) : (
          <div
            className="bg-secondary rounded-circle d-flex align-items-center justify-content-center"
            style={{
              width: "40px",
              height: "40px",
              fontSize: "18px",
              color: "white",
              fontWeight: "bold",
            }}
          >
            {user.full_name?.charAt(0).toUpperCase() || "U"}
          </div>
        )}
        <div>
          <strong>{user.full_name}</strong>
          <br />
          <span style={{ fontSize: "0.85em", color: "#666" }}>{user.email}</span>
        </div>
      </li>
    );
  };


  return (
    <>
      {/* Search Modal */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          height: "100vh",
          width: "100vw",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 1000,
        }}
      >
        <div
          ref={modalRef}
          style={{
            //backgroundColor: "rgb(255, 250, 240)",
            backgroundColor: "#FFFDF0",
            padding: "24px",
            borderRadius: "10px",
            width: "90%",
            maxWidth: "500px",
            maxHeight: "80vh",
            overflowY: "auto",
            position: "relative",
          }}
        >
          <button
            onClick={onClose}
            style={{
              position: "absolute",
              top: "10px",
              right: "10px",
              background: "transparent",
              border: "none",
              fontSize: "20px",
              cursor: "pointer",
            }}
          >
            ❌
          </button>

          <h3 style={{ 
          marginTop: 0,
          fontSize: '20px',
          fontWeight: '700',
          fontStyle: 'bolder',
          color: '#740A03',
          fontFamily: 'Arial, sans-serif',
           }}>Search Users</h3>

          <input
            ref={inputRef}
            type="text"
            placeholder="Type a name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: "100%",
              padding: "10px",
              margin: "10px 0 20px",
              fontSize: "16px",
              border: "1px solid #ccc",
              borderRadius: "6px",
              color: '#280905',
            }}
          />

          {loading && <p>Loading...</p>}
          {error && <p style={{ color: "red" }}>{error}</p>}

          {!loading && !error && (
            <>
              {results.length > 0 ? (
                <ul style={{ 
                  listStyle: "none", 
                  padding: 0,
                  //fontSize: '10px',
                  fontStyle: 'bolder',
                  color: '#740A03',
                  fontFamily: 'Arial, sans-serif', }}>
                  {results.map(renderUserItem)}
                </ul>
              ) : (
                debouncedTerm && 
                <p style={{ 
                  listStyle: "none", 
                  padding: 0,
                  //fontSize: '10px',
                  fontStyle: 'bolder',
                  color: '#740A03',
                  fontFamily: 'Arial, sans-serif', }}>No users found.</p>
              )}

              {suggested.length > 0 && (
                <>
                  <hr style={{ 
                    margin: "20px 0",}} />
                  <h4 style={{ 
                    fontStyle: 'bolder',
                    color: '#740A03',
                    fontFamily: 'Arial, sans-serif',
                    fontStyle: 'bolder',
                    fontWeight: '700'
                    }}>People you may know</h4>
                  <ul style={{ 
                    listStyle: "none", 
                    padding: 0,
                    color: '#740A03',
                    fontFamily: 'Arial, sans-serif', }}>
                    {suggested.map(renderUserItem)}
                  </ul>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Profile Modal */}
      {selectedUser && (
        <ProfileModal
          userId={selectedUser.id}
          suggestedUsers={suggested}
          onClose={closeProfileModal}
          modalRef={profileModalRef}
          actionModalRef={actionModalRef}
        />
      )}
    </>
  );
}

export default SearchModal;
